import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { Guide, GuideImage, GuideLink } from "../HelpCenterTypes";
import { useInternationalizedArticles } from "../data/internationalizedArticles";
import { useTranslation } from "react-i18next";

function HelpCenterGuide() {
  const navigate = useNavigate();
  const { guideSlug, categorySlug } = useParams();
  const { t } = useTranslation("helpCenter");
  const { categories, articles } = useInternationalizedArticles();

  const category = useMemo(
    () => categories.find((cat) => cat.slug === categorySlug),
    [categories, categorySlug]
  );
  const guide = useMemo(
    () =>
      articles.find(
        (g) => g.slug === guideSlug && g.categorySlug === categorySlug
      ),
    [articles, guideSlug, categorySlug]
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  if (!guide) {
    return (
      <div className="flex flex-col items-center p-24 sm:p-40 container">
        <Typography variant="h4" color="error">
          Guide not found
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-24 sm:p-40 container">
      <div className="flex flex-col w-full max-w-4xl">
        <div className="sm:mt-32">
          <Button
            onClick={handleGoBack}
            color="secondary"
            startIcon={
              <FuseSvgIcon>heroicons-outline:arrow-long-left</FuseSvgIcon>
            }
          >
            {t("BACK_TO_GUIDES_AND_RESOURCES")}
          </Button>
        </div>

        {/* Article Header */}
        <div className="mt-8">
          <Typography className="text-4xl sm:text-7xl font-extrabold tracking-tight leading-tight">
            {guide.title}
          </Typography>

          <Typography
            className="mt-8 sm:text-2xl tracking-tight"
            color="text.secondary"
          >
            {guide.subtitle}
          </Typography>

          {/* Article Metadata */}
          {/* <div className="flex flex-wrap items-center gap-16 mt-16">
            {guide.author && (
              <Typography variant="body2" color="text.secondary">
                {t("BY")} {guide.author}
              </Typography>
            )}
            <span className="mx-0">|</span>
            {guide.publishedAt && (
              <Typography variant="body2" color="text.secondary">
                {t("PUBLISHED")} {formatDate(guide.publishedAt)}
              </Typography>
            )}
            {guide.readingTime && (
              <Typography variant="body2" color="text.secondary">
                {guide.readingTime} {t("MIN_READ")}
              </Typography>
            )}
            {guide.featured && (
              <Chip
                label={t("FEATURED")}
                color="primary"
                size="small"
                icon={<FuseSvgIcon>heroicons-outline:star</FuseSvgIcon>}
              />
            )}
          </div> */}

          {/* Tags */}
          {/* {guide.tags && guide.tags.length > 0 && (
            <div className="flex flex-wrap gap-8 mt-16">
              {guide.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  variant="outlined"
                  size="small"
                  className="text-12"
                />
              ))}
            </div>
          )} */}
        </div>

        {/* Article Content */}
        <div className="mt-20 sm:mt-20">
          {/* Images */}
          {/* {guide.images && guide.images.length > 0 && (
            <div className="mb-32">
              {guide.images.map((image: GuideImage) => (
                <Box key={image.id} className="mb-24">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full rounded-lg shadow-lg"
                    style={{ maxHeight: "400px", objectFit: "cover" }}
                  />
                  {image.caption && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className="block mt-8 text-center italic"
                    >
                      {image.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </div>
          )} */}

          {/* Main Content */}
          <div
            className="max-w-none prose dark:prose-invert prose-lg"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: guide.content }}
          />

          {/* Related Links */}
          {/* {guide.links && guide.links.length > 0 && (
            <Card className="mt-32 p-24">
              <Typography variant="h6" className="mb-16">
                {t("RELATED_ARTICLES")}
              </Typography>
              <div className="space-y-12">
                {guide.links.map((link: GuideLink) => (
                  <Box
                    key={link.id}
                    className="p-16 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Link
                      href={link.url}
                      className="text-lg font-medium no-underline hover:underline"
                      color="primary"
                    >
                      {link.title}
                    </Link>
                    {link.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="mt-4"
                      >
                        {link.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </div>
            </Card>
          )} */}
        </div>

        {/* Article Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-40 pt-32 border-t">
          <Typography className="text-sm font-medium" color="text.secondary">
            {guide.updatedAt
              ? `${t("LAST_UPDATED")} ${formatDate(guide.updatedAt)}`
              : t("RECENTLY_UPDATED")}
          </Typography>
          {/* <div className="flex items-center mt-8 sm:mt-0">
            <Typography className="font-medium mx-8" color="text.secondary">
              {t("WAS_THIS_PAGE_HELPFUL")}
            </Typography>
            <IconButton>
              <FuseSvgIcon>heroicons-outline:thumb-up</FuseSvgIcon>
            </IconButton>
            <IconButton>
              <FuseSvgIcon>heroicons-outline:thumb-down</FuseSvgIcon>
            </IconButton>
          </div> */}
        </div>

        {/* Next Article Card */}
        {/* <Card className="mt-32 flex items-center justify-between p-24 sm:px-40 rounded-2xl shadow hover:shadow-lg transition-shadow ease-in-out duration-150">
          <div>
            <Typography color="text.secondary">{t("NEXT")}</Typography>
            <Typography className="text-lg font-semibold">
              {t("REMOVING_MEDIA")}
            </Typography>
          </div>
          <FuseSvgIcon className="ml-12">
            heroicons-outline:arrow-right
          </FuseSvgIcon>
        </Card> */}
      </div>
    </div>
  );
}

export default HelpCenterGuide;
